<template>
  <div>
    <div class="flex justify-between items-baseline">
      <label
        v-if="label !== null"
        class="block text-sm leading-0 font-medium text-gray-700"
      >
        {{ label }}
      </label>

      <div
        v-if="selectedItem"
        @click="resetPlayer"
      >
        <div class="flex items-center justify-center w-5 h-5 text-xs bg-white rounded-lg border leading-none text-red-400 cursor-pointer hover:text-red-700 transition-all duration-300">
          <i
            class="far fa-times"
            style="margin-bottom: -1px;"
          />
        </div>
      </div>
    </div>
    <div
      class="py-1"
    >
      <Search
        :results="dropdownResults"
        :loading="loading"
        :total-count="totalCount"
        :placeholder="placeholder"
        :show-no-results="false"
        :select-item="selectedItem"
        dropdown-height="max-h-36"
        @input="searchInput"
        @skip="skip"
        @select="selectPlayer($event, true)"
        @reset="resetPlayer"
      />
    </div>

    <div class="py-1">
      <TypeDropdown
        :type="player.type"
        :disabled="disabled"
        @input="typeInput"
      />
    </div>

    <div class="py-1">
      <div
        class="flex justify-between relative w-full rounded-md border border-gray-300 bg-white pl-3 pr-3 py-2 text-left  focus:shadow-outline-blue focus:border-blue-300 transition ease-in-out duration-150 sm:text-sm sm:leading-5"
      >
        <input
          v-model="player.telephoneNumber"
          class="focus:outline-none bg-white"
          placeholder="Phone number"
          :disabled="disabled"
        >

        <div v-if="disabled">
          <i class="fas fa-lock text-gray-400" />
        </div>
      </div>
    </div>

    <div class="py-1">
      <div
        class="flex justify-between relative w-full rounded-md border border-gray-300 bg-white pl-3 pr-3 py-2 text-left  focus:shadow-outline-blue focus:border-blue-300 transition ease-in-out duration-150 sm:text-sm sm:leading-5"
      >
        <input
          v-model="player.emailAddress"
          class="focus:outline-none bg-white"
          placeholder="Email address"
          :disabled="disabled"
        >

        <div v-if="disabled">
          <i class="fas fa-lock text-gray-400" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PrimaryButton from '@/app/shared/components/base/button/primaryButton.vue';
import SecondaryButton from '@/app/shared/components/base/button/secondaryButton.vue';
import Search from '@/app/shared/components/search/search.vue';
import TypeDropdown from '@/app/shared/components/player/typeDropdown.vue';
import { GetAllPlayers, MapType, GetPlayer } from '@/app/shared/services/playerService/playerService';
import { RemovePlayer } from '@/app/shared/services/bookingService/bookingService';
import { GetContact } from '@/app/shared/services/contactService/contactService';

export default {
  name: 'PlayerSelection',

  components: {
    PrimaryButton,
    SecondaryButton,
    Search,
    TypeDropdown,
  },

  props: {
    placeholder: {
      type: String,
      default: 'Search for an exisiting player',
    },
    label: {
      type: String,
      default: null,
    },
    index: {
      type: Number,
      default: 0,
    },
    defaultPlayer: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      selectedPlayer: null,
      selectedItem: null,
      playerContactDetails: null,
      search: '',
      skipCount: 0,
      maxCount: 5,
      totalCount: undefined,
      loading: true,
      resetLoading: false,
      results: [],

      player: {
        name: '',
        type: null,
        emailAddress: '',
        telephoneNumber: '',
      },
    };
  },

  computed: {
    disabled() {
      return this.selectedPlayer !== null;
    },

    dropdownResults() {
      const dropdownResults = [];

      this.results.forEach((player) => {
        dropdownResults.push({
          primaryText: player.name,
          secondaryText: MapType(player.type),
        });
      });

      return dropdownResults;
    },
  },

  watch: {
    defaultPlayer() {
      if (this.defaultPlayer !== null) {
        this.selectDefaultPlayer(this.defaultPlayer);
      }
    },

    search() {
      this.getPlayers();
    },

    player: {
      handler(item) {
        if (this.defaultPlayer === null) {
          this.$emit('input', {
            index: this.index,
            player: this.player,
          });
        }
      },
      deep: true,
    },
  },

  mounted() {
    if (this.defaultPlayer !== null) {
      this.selectDefaultPlayer(this.defaultPlayer);
    }
  },

  methods: {
    selectDefaultPlayer(player) {
      GetPlayer(player.id).then((data) => {
        this.selectPlayer(data, false);
      });
    },

    getPlayers() {
      this.loading = true;
      GetAllPlayers(this.search, true, this.skipCount, this.maxCount).then((result) => {
        if (this.skipCount === 0) {
          this.results = result.items;
          this.totalCount = result.totalCount;
        } else {
          result.items.forEach((item) => {
            this.results.push(item);
          });
        }
        this.loading = false;
      });
    },

    resetPlayer() {
      if (this.defaultPlayer !== null) {
        if (!this.resetLoading) {
          this.resetLoading = true;
          this.$emit('removePlayer', this.defaultPlayer.id);
          RemovePlayer(this.defaultPlayer.bookingId, this.defaultPlayer.id).then(() => {
            this.player = {
              name: '',
              type: null,
              emailAddress: '',
              telephoneNumber: '',
            };

            this.selectedPlayer = null;
            this.resetLoading = false;
          });
        }
      } else {
        this.player = {
          name: '',
          type: null,
          emailAddress: '',
          telephoneNumber: '',
        };

        this.selectedPlayer = null;
      }
    },

    searchInput(input) {
      this.skipCount = 0;
      this.search = input;
      this.player.name = input;
    },

    typeInput(type) {
      this.player.type = type;
    },

    skip() {
      this.skipCount++;
      this.getPlayers();
    },

    selectPlayer(player, dropdownSelect) {
      if (dropdownSelect) {
        const result = this.results.filter(p => p.name === player.primaryText);
        this.selectedPlayer = result[0];
      } else {
        this.selectedPlayer = player;
        this.selectedItem = {
          primaryText: player.name,
          secondaryText: MapType(player.type),
        };
      }

      GetContact(this.selectedPlayer.contactDetailsId).then((data) => {
        this.playerContactDetails = data;
        this.player.id = this.selectedPlayer.id;
        this.player.name = this.selectedPlayer.name;
        this.player.type = this.selectedPlayer.type;
        this.player.emailAddress = data.emailAddress;
        this.player.telephoneNumber = data.telephoneNumber;
      });
    },
  },
};
</script>
